import type { ViewMode, YearKey } from './types';

interface ControlsProps {
  viewMode: ViewMode;
  selectedYear: YearKey;
  onViewModeChange: (mode: ViewMode) => void;
  onYearChange: (year: YearKey) => void;
  onPreset: (preset: 'current' | 'minimum-distance') => void;
  onSave: () => void;
  onLoad: () => void;
}

const YEARS: { value: YearKey; label: string }[] = [
  { value: '2026', label: '2026' },
  { value: '2027', label: '2027' },
  { value: '2028', label: '2028' },
  { value: '2029', label: '2029' },
  { value: '2030', label: '2030' },
  { value: '2031', label: '2031' },
  { value: '2032', label: '2032' },
  { value: '2033', label: '2033' },
  { value: '2034', label: '2034' },
  { value: '2035', label: '2035' },
  { value: '2036', label: '2036' },
];

export function Controls({
  viewMode,
  selectedYear,
  onViewModeChange,
  onYearChange,
  onPreset,
  onSave,
  onLoad,
}: ControlsProps) {
  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div>
        <label className="block text-xs font-semibold text-davis-slate uppercase tracking-wide mb-2">
          Map View
        </label>
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => onViewModeChange('zones')}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              viewMode === 'zones'
                ? 'bg-davis-navy text-white'
                : 'bg-white text-davis-slate hover:bg-gray-50'
            }`}
          >
            School Zones
          </button>
          <button
            onClick={() => onViewModeChange('heatmap')}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              viewMode === 'heatmap'
                ? 'bg-davis-navy text-white'
                : 'bg-white text-davis-slate hover:bg-gray-50'
            }`}
          >
            Distance Heatmap
          </button>
        </div>
      </div>

      {/* Heatmap Legend */}
      {viewMode === 'heatmap' && (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs font-semibold text-davis-slate mb-2">
            Distance to Assigned School
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-davis-slate">0 mi</span>
            <div
              className="flex-1 h-3 rounded-full"
              style={{
                background:
                  'linear-gradient(to right, rgb(0,200,60) 0%, rgb(255,220,0) 35%, rgb(255,100,0) 65%, rgb(210,0,0) 100%)',
              }}
            />
            <span className="text-xs text-davis-slate">3+ mi</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-davis-slate/60">close</span>
            <span className="text-[10px] text-davis-slate/60">far</span>
          </div>
        </div>
      )}

      {/* Year Selector */}
      <div>
        <label className="block text-xs font-semibold text-davis-slate uppercase tracking-wide mb-2">
          Projected Population Year
        </label>
        <select
          value={selectedYear}
          onChange={(e) => onYearChange(e.target.value as YearKey)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-davis-blue focus:border-transparent"
        >
          {YEARS.map((y) => (
            <option key={y.value} value={y.value}>
              {y.label}
            </option>
          ))}
        </select>
      </div>

      {/* Presets */}
      <div>
        <label className="block text-xs font-semibold text-davis-slate uppercase tracking-wide mb-2">
          Map Presets
        </label>
        <div className="space-y-2">
          <button
            onClick={() => onPreset('current')}
            className="w-full px-3 py-2 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-davis-charcoal"
          >
            Current Boundaries
          </button>
          <button
            onClick={() => onPreset('minimum-distance')}
            className="w-full px-3 py-2 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-davis-charcoal"
          >
            Minimum Distance
          </button>
        </div>
      </div>

      {/* Save/Load */}
      <div>
        <label className="block text-xs font-semibold text-davis-slate uppercase tracking-wide mb-2">
          Custom Configuration
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onSave}
            className="px-3 py-2 text-xs font-medium bg-davis-navy text-white rounded-lg hover:bg-davis-navy-light transition-colors"
          >
            Save Map
          </button>
          <button
            onClick={onLoad}
            className="px-3 py-2 text-xs font-medium bg-white border border-davis-navy text-davis-navy rounded-lg hover:bg-davis-navy/5 transition-colors"
          >
            Load Map
          </button>
        </div>
      </div>
    </div>
  );
}
